import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  GenericJsonTable,
  DataToolbar,
  Pagination,
  JsonViewer,
} from '@/components/data';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  fetchGatewayData,
  humanizeKey,
  exportToCsv,
} from '@/lib/integrations';
import type { ColumnConfig, GatewayResponse } from '@/lib/integrations/types';

export const Route = createFileRoute('/_dashboard/data/$')({
  component: DataBrowserPage,
});

const STORAGE_KEY_PREFIX = 'data-browser-columns-';

function DataBrowserPage() {
  const { _splat: path } = Route.useParams();
  const navigate = useNavigate();

  // Parse path: first segment is alias, rest is endpoint
  const pathSegments = path?.split('/').filter(Boolean) || [];
  const alias = pathSegments[0] || '';
  const endpoint = '/' + pathSegments.slice(1).join('/');

  // State
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedRow, setSelectedRow] = useState<Record<string, unknown> | null>(null);
  const [metadata, setMetadata] = useState<GatewayResponse['metadata'] | null>(null);

  // Storage key for column config
  const storageKey = `${STORAGE_KEY_PREFIX}${alias}-${endpoint}`;

  const initColumnsFromData = useCallback((items: Record<string, unknown>[]) => {
    const cols = Object.keys(items[0]).map((key, index) => ({
      key,
      label: humanizeKey(key),
      visible: true,
      order: index,
    }));
    setColumns(cols);
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!alias || !endpoint || endpoint === '/') {
      setError(new Error('Invalid path. Expected format: /data/{alias}/{endpoint}'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchGatewayData(alias, endpoint);

      // Handle different response shapes
      let items: Record<string, unknown>[] = [];
      if (Array.isArray(response.data)) {
        items = response.data as Record<string, unknown>[];
      } else if (response.data && typeof response.data === 'object') {
        // Single object or wrapped response
        const responseData = response.data as Record<string, unknown>;
        // Try common wrapper keys
        if (Array.isArray(responseData.items)) {
          items = responseData.items as Record<string, unknown>[];
        } else if (Array.isArray(responseData.data)) {
          items = responseData.data as Record<string, unknown>[];
        } else if (Array.isArray(responseData.records)) {
          items = responseData.records as Record<string, unknown>[];
        } else if (Array.isArray(responseData.results)) {
          items = responseData.results as Record<string, unknown>[];
        } else {
          // Single object, wrap in array
          items = [responseData];
        }
      }

      setData(items);
      setMetadata(response.metadata);

      // Initialize columns from data if not loaded from storage
      if (items.length > 0) {
        const savedColumns = localStorage.getItem(storageKey);
        if (savedColumns) {
          try {
            setColumns(JSON.parse(savedColumns));
          } catch {
            initColumnsFromData(items);
          }
        } else {
          initColumnsFromData(items);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [alias, endpoint, storageKey, initColumnsFromData]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save columns to localStorage when they change
  useEffect(() => {
    if (columns.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    }
  }, [columns, storageKey]);

  // Filter data by search
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return data;

    const search = searchValue.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(search);
      })
    );
  }, [data, searchValue]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchValue]);

  // Handlers
  const handleColumnsChange = (newColumns: ColumnConfig[]) => {
    setColumns(newColumns);
  };

  const handleExport = () => {
    const visibleCols = columns.filter((c) => c.visible);
    exportToCsv(filteredData, visibleCols, `${alias}-${endpoint.replace(/\//g, '-')}.csv`);
    toast.success('Data exported to CSV');
  };

  const handleRefresh = () => {
    fetchData();
    toast.success('Data refreshed');
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    setSelectedRow(row);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/data' })}>
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Database className="size-5" />
          <h1 className="text-2xl font-bold">
            {alias}
            <span className="text-muted-foreground font-normal">{endpoint}</span>
          </h1>
        </div>
      </div>

      {/* Metadata */}
      {metadata && (
        <div className="text-sm text-muted-foreground">
          Fetched in {metadata.durationMs}ms
        </div>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="flex items-center justify-between">
            <span>{filteredData.length} records</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataToolbar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            columns={columns}
            onColumnsChange={handleColumnsChange}
            onExport={handleExport}
            onRefresh={handleRefresh}
            loading={loading}
          />

          <GenericJsonTable
            data={paginatedData}
            loading={loading}
            error={error}
            columns={columns}
            onColumnsChange={handleColumnsChange}
            onRowClick={handleRowClick}
          />

          {!loading && !error && filteredData.length > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={filteredData.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Row Detail Dialog */}
      <Dialog open={selectedRow !== null} onOpenChange={() => setSelectedRow(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Record Details</DialogTitle>
          </DialogHeader>
          <JsonViewer data={selectedRow} collapsed={false} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
